import * as Sentry from "@sentry/react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { DateTime } from "luxon";
import { enqueueSnackbar } from "notistack";

import { QUERY_KEYS, useGetTransportAxiosInstance } from "@/constants";
import { DriverColumnStatus } from "@/constants/driverStatuses";
import { decodeArrayBufferToString } from "@/helpers/strings";
import { queryClient } from "@/main";
import { IDriverStatusForm } from "@/pages/DriverStatus.tsx/components/DriverStatusInfoPopup";
import useAuth from "@/providers/hooks/useAuth";
import { DriverStatus, RES_DriverStatus, RES_Shift, Shift } from "@/types/daily-status.types";
import { CommonExportReq } from "@/types/export.types";
import { UserType } from "@/types/user.types";

const formatShift = ({
    startTime,
    endTime,
    createdAt,
    updatedAt,
    ...status
}: DriverStatus<false>): DriverStatus<true> => {
    return {
        ...status,
        startTime: DateTime.fromISO(startTime),
        endTime: (endTime && DateTime.fromISO(endTime)) || undefined,
        createdAt: (createdAt && DateTime.fromISO(createdAt)) || undefined,
        updatedAt: (updatedAt && DateTime.fromISO(updatedAt)) || undefined,
    };
};
export const useCreateShiftMutation = () => {
    const axiosInstance = useGetTransportAxiosInstance();
    return useMutation<Shift, unknown, IDriverStatusForm>({
        mutationFn: async ({ vehicleId, userId, note, dateRange, status }) => {
            const [startTime, endTime] = dateRange;
            try {
                const res = await axiosInstance.post<RES_Shift>("/driver-status/", {
                    status,
                    vehicleId,
                    userId,
                    note,
                    startTime: startTime?.toISO(),
                    endTime: endTime ? endTime.toISO() : null,
                });

                return {
                    id: res.data.id,
                    driverId: res.data.driverId,
                    userId: res.data.userId,
                    isActive: res.data.isActive,
                    startTime: DateTime.fromISO(res.data.startTime),
                    endTime: res.data.endTime ? DateTime.fromISO(res.data.endTime) : null,
                    vehicleId: res.data.vehicleId,
                };
            } catch (err) {
                Sentry.captureException(err);

                enqueueSnackbar((err as AxiosError<{ message: string }>)?.response?.data?.message, {
                    variant: "error",
                });
                throw new Error((err as AxiosError<{ message: string }>)?.response?.data?.message);
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.GET_DAILY_STATUS_REPORTS],
            });
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.GET_DRIVERS, true],
            });
        },
    });
};
export const useUpdateShiftMutation = () => {
    const axiosInstance = useGetTransportAxiosInstance();
    return useMutation<Shift, unknown, IDriverStatusForm & { id: string }>({
        mutationFn: async ({ id, vehicleId, userId, note, dateRange, status }) => {
            const [startTime, endTime] = dateRange;
            try {
                const res = await axiosInstance.put<RES_Shift>("/driver-status/" + id, {
                    status,
                    vehicleId,
                    userId,
                    note,
                    startTime: startTime?.toISO(),
                    endTime: endTime ? endTime.toISO() : null,
                });

                return {
                    id: res.data.id,
                    driverId: res.data.driverId,
                    userId: res.data.userId,
                    isActive: res.data.isActive,
                    startTime: DateTime.fromISO(res.data.startTime),
                    endTime: res.data.endTime ? DateTime.fromISO(res.data.endTime) : null,
                    vehicleId: res.data.vehicleId,
                };
            } catch (err) {
                Sentry.captureException(err);
                enqueueSnackbar((err as AxiosError<{ message: string }>)?.response?.data?.message, {
                    variant: "error",
                });
                throw new Error((err as AxiosError<{ message: string }>)?.response?.data?.message);
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.GET_DAILY_STATUS_REPORTS],
            });
        },
    });
};

export const useCompleteShiftMutation = () => {
    const axiosInstance = useGetTransportAxiosInstance();
    return useMutation<Shift, unknown, { userId: string }>({
        mutationFn: async ({ userId }) => {
            try {
                const res = await axiosInstance.put<RES_Shift>("/driver-status/complete/" + userId, {});

                return {
                    id: res.data.id,
                    driverId: res.data.driverId,
                    isActive: res.data.isActive,
                    startTime: DateTime.fromISO(res.data.startTime),
                    endTime: res.data.endTime ? DateTime.fromISO(res.data.endTime) : null,
                    vehicleId: res.data.vehicleId,
                };
            } catch (err) {
                Sentry.captureException(err);

                enqueueSnackbar((err as AxiosError<{ message: string }>)?.response?.data?.message, {
                    variant: "error",
                });
                throw new Error((err as AxiosError<{ message: string }>)?.response?.data?.message);
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.GET_DAILY_STATUS_REPORTS],
            });
        },
    });
};
export const useStartShiftMutation = () => {
    const axiosInstance = useGetTransportAxiosInstance();
    return useMutation<DriverStatus, unknown, { longitude: number; latitude: number }>({
        mutationFn: async (payload) => {
            try {
                const res = await axiosInstance.post<DriverStatus<false>>("/driver-status/start", payload);

                return formatShift(res.data);
            } catch (err) {
                Sentry.captureException(err);

                enqueueSnackbar((err as AxiosError<{ message: string }>)?.response?.data?.message, {
                    variant: "error",
                });
                throw new Error((err as AxiosError<{ message: string }>)?.response?.data?.message);
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.GET_DAILY_STATUS_REPORTS],
            });
        },
    });
};
export const useStartFreeDayMutation = () => {
    const axiosInstance = useGetTransportAxiosInstance();
    return useMutation<DriverStatus, unknown>({
        mutationFn: async () => {
            try {
                const res = await axiosInstance.post<DriverStatus<false>>("/driver-status/driver/start-free-day");

                return formatShift(res.data);
            } catch (err) {
                Sentry.captureException(err);

                enqueueSnackbar((err as AxiosError<{ message: string }>)?.response?.data?.message, {
                    variant: "error",
                });
                throw new Error((err as AxiosError<{ message: string }>)?.response?.data?.message);
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.GET_DAILY_STATUS_REPORTS],
            });
        },
    });
};
export const useStopShiftMutation = () => {
    const axiosInstance = useGetTransportAxiosInstance();
    return useMutation<DriverStatus, unknown, { longitude: number; latitude: number }>({
        mutationFn: async (payload) => {
            try {
                const res = await axiosInstance.post<DriverStatus<false>>("/driver-status/driver/stop", payload);

                return formatShift(res.data);
            } catch (err) {
                Sentry.captureException(err);

                enqueueSnackbar((err as AxiosError<{ message: string }>)?.response?.data?.message, {
                    variant: "error",
                });
                throw new Error((err as AxiosError<{ message: string }>)?.response?.data?.message);
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.GET_DAILY_STATUS_REPORTS],
            });
        },
    });
};

export const useDeleteShiftMutation = () => {
    const axiosInstance = useGetTransportAxiosInstance();
    return useMutation<boolean, unknown, string>({
        mutationFn: async (id) => {
            try {
                const res = await axiosInstance.delete<boolean>("/driver-status/" + id);

                return res.data;
            } catch (err) {
                Sentry.captureException(err);
                enqueueSnackbar((err as AxiosError<{ message: string }>)?.response?.data?.message, {
                    variant: "error",
                });
                throw new Error((err as AxiosError<{ message: string }>)?.response?.data?.message);
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.GET_DAILY_STATUS_REPORTS],
            });
        },
    });
};

export const useGetDailyStatusList = ({ month }: { month: DateTime }) => {
    const { auth } = useAuth();
    const axiosInstance = useGetTransportAxiosInstance();

    return useQuery<DriverStatus[]>({
        enabled: !!auth?.accessToken,
        queryKey: [QUERY_KEYS.GET_DAILY_STATUS_REPORTS, month],
        queryFn: () =>
            axiosInstance
                .get<RES_DriverStatus>("/driver-status/", {
                    params: {
                        startTime: month.startOf("month").toISODate(),
                        endTime: month.endOf("month").toISODate(),
                    },
                })
                .then((res): DriverStatus[] => {
                    return res.data.statuses.map((item) => formatShift(item));
                }),
    });
};
export const useGetDailyStatusCurrentShift = () => {
    const { auth } = useAuth();
    const axiosInstance = useGetTransportAxiosInstance();

    return useQuery<{ shift?: DriverStatus | null; userType: UserType }>({
        enabled: !!auth?.accessToken,
        queryKey: [QUERY_KEYS.GET_CURRENT_SHIFT],
        queryFn: () =>
            axiosInstance
                .get<{
                    shift?: DriverStatus<false>;
                    message: string;
                    userType: UserType;
                }>("/driver-status/driver/current")
                .then((res) => {
                    const shift = res.data.shift;
                    return { shift: shift ? formatShift(shift) : null, userType: res.data.userType };
                }),
    });
};

interface ExportDailyStatusParams extends CommonExportReq {
    startTime: string;
    endTime: string;
    statusTexts: { [key in DriverColumnStatus]: string };
    userIds: string[];
}

export const useExportDailyStatusList = () => {
    const axiosInstance = useGetTransportAxiosInstance();

    return useMutation<ArrayBuffer, Error, ExportDailyStatusParams>({
        mutationFn: (payload) => {
            return axiosInstance
                .post<ArrayBuffer>("/driver-status/export", payload, {
                    responseType: "arraybuffer",
                })
                .then((res) => res.data)
                .catch((err) => {
                    Sentry.captureException(err);
                    const errorBuffer = (err as AxiosError<ArrayBuffer>)?.response?.data as ArrayBuffer;
                    if (errorBuffer) {
                        const errorMessage = decodeArrayBufferToString(errorBuffer);
                        enqueueSnackbar(errorMessage, {
                            variant: "error",
                        });
                    }
                    throw err;
                });
        },
    });
};
